/**
 * Drawer UI Manager
 *
 * This module manages the lifecycle, rendering, and user interactions for the bottom drawer component.
 *
 * Features:
 * - Handles opening, closing, and updating the drawer's HTML content via a service and Redux state.
 * - Manages drag-to-close gesture with smooth animation and configurable threshold.
 * - Registers and disconnects callbacks for drawer lifecycle events (open, close, HTML ready, HTML removed).
 * - Integrates with an overlay component to provide modal-like behavior.
 * - Emits and listens for custom events to coordinate with other parts of the application.
 * - Uses RxJS streams to react to drawer actions and pointer events.
 *
 * The drawer is intended for use as a bottom sheet in the UI,
 * supporting both programmatic and user-driven interactions.
 */
import { EMPTY, filter, fromEvent, merge, tap } from 'rxjs';
import { store } from '@redux/store';
import { onDocumentPointerMove$, onDocumentPointerUp$ } from 'layout/redux/observables';
import { selectDrawer } from 'layout/redux/selectors';
import drawerService from '../services/drawerService';
import Overlay from './overlay';

/**
 * How long a suppressed-click flag stays armed after a tap-to-expand, in ms — see
 * suppressClickUntil below. The click that actually needs catching normally follows pointerup
 * within single-digit ms (same input-event flush), but some mobile browsers still carry a legacy
 * ~300ms delay before dispatching it on elements without an explicit touch-action (historically
 * used to disambiguate a single tap from a double-tap-to-zoom). This adds headroom over that
 * worst case while still being short enough that a genuine, unrelated second interaction is very
 * unlikely to land inside the same window.
 */
const CLICK_SUPPRESSION_WINDOW_MS = 400;

const drawerConfiguration = {
  startY: 0,
  currentY: 0,
  isDragging: false,
  threshold: 100,
  animationFrameId: null,
  latestDelta: 0,
  strategy: null,
  isFullSize: false,
  suppressClickUntil: 0,
};

const disconnectedCallbacks = new Map();
const registeredCallbacks = new Map();
const overlay = new Overlay();
const drawer$ = drawerService.getHtml();
const drawerNode = document.querySelector('[data-drawer]');
const drawerContainer = drawerNode?.querySelector('[data-drawer-content]');
const isDrawerReady = () => Boolean(drawerNode && drawerContainer);
const onDrawerPointerDown$ = drawerNode
  ? fromEvent(drawerNode, 'pointerdown')
  : EMPTY;
const onDrawerClick$ = drawerNode
  ? fromEvent(drawerNode, 'click', { capture: true })
  : EMPTY;

/**
 * Reflects the drawer's expanded state on the `nhs-drawer--open` class (which drives styling) and
 * mirrors it onto the `data-drawer-open` attribute — a stable hook other modules can observe
 * without coupling to the internal class name.
 * @param {boolean} isExpanded
 */
const setDrawerExpanded = (isExpanded) => {
  drawerNode.classList.toggle('nhs-drawer--open', isExpanded);
  drawerNode.dataset.drawerOpen = String(isExpanded);
};

/**
 * Gesture strategies — implement the Strategy Pattern for pointer interactions.
 *
 * Activate via `drawerService.mountElement(el, { peek: '72px' })`.
 * To add a new strategy: write a factory matching the interface below, then
 * resolve it from `options` in the `mountElement` handler.
 *
 * Interface:
 *   activate()     — sets the drawer's initial visible state on mount.
 *   onSwipeDown()  — optional; called when drag exceeds `drawerConfiguration.threshold`.
 *                    Omit to use the default behaviour: close the drawer entirely.
 *   onTap()        — called when drag is within threshold (snap-back or expand).
 *   deactivate()   — reverts strategy-specific DOM state on close.
 */
const createDefaultStrategy = () => ({
  activate: () => setDrawerExpanded(true),
  onTap: () => {
    drawerNode.style.removeProperty('--drag-translate');
    setDrawerExpanded(true);
  },
  deactivate: () => {},
});

const createPeekStrategy = (peekHeight) => ({
  activate: () => {
    drawerNode.style.setProperty('--drawer-peek-height', peekHeight);
    drawerNode.classList.add('nhs-drawer--collapsed');
    setDrawerExpanded(false);
  },
  onSwipeDown: () => {
    setDrawerExpanded(false);
    drawerNode.style.removeProperty('--drag-translate');
    drawerNode.classList.add('nhs-drawer--collapsed');
  },
  onTap: () => {
    drawerNode.style.removeProperty('--drag-translate');
    drawerNode.classList.remove('nhs-drawer--collapsed');
    setDrawerExpanded(true);
  },
  deactivate: () => {
    drawerNode.classList.remove('nhs-drawer--collapsed');
    drawerNode.style.removeProperty('--drawer-peek-height');
  },
});

drawerConfiguration.strategy = createDefaultStrategy();

/**
 * Executes and cleans up all disconnected callbacks registered under the given callback name.
 *
 * - Retrieves all disconnected callbacks for the specified callbackName from the disconnectedCallbacks map.
 * - Executes each callback function if it is a function.
 * - Removes the callbackName entry from both disconnectedCallbacks and registeredCallbacks maps to
 *   prevent memory leaks.
 *
 * This function is typically called when the drawer is closed or removed from the DOM,
 * ensuring that any teardown or cleanup logic associated with the drawer is properly executed.
 *
 * @param {string} callbackName - The name/key under which the disconnected callbacks are registered.
 */
const onDisconnected = (callbackName) => {
  const callbacksDisconnected = disconnectedCallbacks.get(callbackName) ?? [];
  callbacksDisconnected.filter((fn) => typeof fn === 'function').forEach((fn) => fn());
  if (disconnectedCallbacks.has(callbackName)) {
    disconnectedCallbacks.delete(callbackName);
    registeredCallbacks.delete(callbackName);
  }
};

/**
 * Closes the drawer UI component and performs all necessary cleanup.
 *
 * - Retrieves the current drawer information from the Redux store.
 * - Executes and cleans up any disconnected callbacks associated with the drawer.
 * - Animates the drawer out of view by translating it down.
 * - Removes the overlay component from the DOM.
 * - Clears the drawer's HTML content.
 * - Dispatches a custom 'drawerClosed' event on the document body with the drawer information as event detail,
 *   after a short delay to allow for animation completion.
 *
 * This function ensures that the drawer is properly closed, all resources are released,
 * and other parts of the application can react to the drawer being closed.
 */
const closeDrawer = () => {
  const drawerInformation = selectDrawer(store.getState());
  const callbackName = drawerInformation?.name;

  onDisconnected(callbackName);
  drawerConfiguration.strategy.deactivate();
  drawerConfiguration.strategy = createDefaultStrategy();
  drawerConfiguration.isFullSize = false;
  drawerNode.classList.remove('nhs-drawer--dragging', 'nhs-drawer--full');
  setDrawerExpanded(false);
  drawerNode.style.removeProperty('--drag-translate');
  overlay.remove();
  drawerContainer.innerHTML = null;

  document.body.dispatchEvent(
    new CustomEvent('drawerClosed', { detail: { drawerInformation } })
  );
};

/**
 * Registers a callback function to be executed when the drawer is disconnected (removed from the DOM).
 *
 * - Adds the provided callback function to the list of disconnected callbacks for the given drawer name.
 * - If callbacks already exist for the drawer name, the new callback is added to the beginning of the list.
 * - These callbacks will be executed and cleaned up by `onDisconnected` when the drawer is closed or removed.
 *
 * @param {Object} data - The data object containing the drawer name and callback function.
 * @param {string} data.name - The unique name/key for the drawer instance.
 * @param {Function} data.callback - The callback function to register for disconnection.
 */
const disconnectCallback = (data) => {
  const existingCallbacks = disconnectedCallbacks.get(data.name) ?? [];
  disconnectedCallbacks.set(data.name, [data.callback, ...existingCallbacks]);
};

/**
 * Opens the drawer UI component and injects the provided HTML content.
 *
 * - Retrieves the current drawer name from the Redux store.
 * - Sets the drawer's innerHTML to the provided HTML string.
 * - Animates the drawer into view by translating it to position 0.
 * - Creates and displays the overlay component.
 * - Executes any registered callbacks associated with the drawer's name (e.g., for setup or event binding).
 *
 * This function is responsible for rendering and displaying the drawer,
 * ensuring it is interactive and ready for user interaction.
 *
 * @param {Object} html - An object containing the HTML string to inject into the
 * drawer (typically { value: '<html>' }).
 */
const openDrawer = (html) => {
  const callbackName = selectDrawer(store.getState())?.name;
  const callbacksRegistered = registeredCallbacks.get(callbackName) ?? [];

  drawerContainer.innerHTML = html.value;
  setDrawerExpanded(true);
  overlay.create();
  callbacksRegistered.filter((fn) => typeof fn === 'function').forEach((fn) => fn());
};

/**
 * Registers a callback function to be executed when the drawer's HTML is ready (inserted into the DOM).
 *
 * - Adds the provided callback function to the list of registered callbacks for the given drawer name.
 * - If callbacks already exist for the drawer name, the new callback is added to the beginning of the list.
 * - These callbacks will be executed when the drawer is opened and its HTML is rendered.
 *
 * @param {Object} data - The data object containing the drawer name and callback function.
 * @param {string} data.name - The unique name/key for the drawer instance.
 * @param {Function} data.callback - The callback function to register for when the drawer's HTML is ready.
 */
const registerCallback = (data) => {
  const existingCallbacks = registeredCallbacks.get(data.name) ?? [];
  registeredCallbacks.set(data.name, [data.callback, ...existingCallbacks]);
};

/**
 * Updates the vertical position of the drawer during a drag gesture.
 *
 * - Uses the latest delta value from the drawerConfiguration to set the drawer's CSS transform.
 * - Called via requestAnimationFrame for smooth, performant updates as the user drags the drawer.
 * - Resets the animationFrameId to null after updating the position.
 *
 * This function enables smooth, real-time feedback when the user drags the drawer,
 * supporting the drag-to-close gesture.
 */
const updateDrawerPosition = () => {
  drawerConfiguration.animationFrameId = null;
  drawerNode.style.setProperty(
    '--drag-translate',
    `${drawerConfiguration.latestDelta}px`
  );
};

/**
 * Handles the pointer down event on the drawer to initiate a drag gesture.
 *
 * - Sets the isDragging flag to true in the drawer configuration.
 * - Records the starting Y position of the pointer.
 * - Disables CSS transition for the drawer to allow smooth, real-time dragging.
 *
 * This function is called when the user presses down on the drawer,
 * preparing the drawer for a drag-to-close gesture.
 *
 * @param {PointerEvent} event - The pointerdown event object.
 */
const handleDrawerPointerDown = (event) => {
  if (drawerConfiguration.isFullSize) {
    return;
  }

  // Suppresses the browser's compatibility click event that would otherwise fire right after
  // this gesture resolves (touch pointers only — preventDefault() here has no effect on mouse
  // pointers, which never dispatch synthetic compatibility events in the first place). Needed
  // because onTap() below synchronously changes the drawer's layout (e.g. expanding out of the
  // collapsed peek state) — without this, the click that follows resolves its target against
  // that already-changed layout instead of whatever was actually under the pointer, so a tap
  // that only meant to expand the drawer can also "click" content newly revealed underneath it.
  event.preventDefault();

  drawerConfiguration.isDragging = true;
  drawerConfiguration.startY = event.clientY;
  drawerNode.classList.add('nhs-drawer--dragging');
};

/**
 * Handles the pointer move event on the window to update the drawer's position during a drag gesture.
 *
 * - Checks if a drag operation is in progress (isDragging).
 * - Calculates the vertical delta between the current pointer position and the drag start position.
 * - If the delta is positive, updates the drawer's latestDelta and currentY values.
 * - Uses requestAnimationFrame to smoothly update the drawer's position via updateDrawerPosition.
 *
 * This function enables real-time, performant feedback as the user drags the drawer,
 * supporting the drag-to-close gesture.
 *
 * @param {PointerEvent} event - The pointermove event object.
 */
const handleWindowPointerMove = (event) => {
  if (!drawerConfiguration.isDragging) {
    return;
  }

  const delta = event.clientY - drawerConfiguration.startY;
  if (delta > 0) {
    drawerConfiguration.latestDelta = delta;
    drawerConfiguration.currentY = delta;

    if (!drawerConfiguration.animationFrameId) {
      drawerConfiguration.animationFrameId = requestAnimationFrame(updateDrawerPosition);
    }
  }
};

/**
 * True while drawerConfiguration.suppressClickUntil (a short-lived flag rather than a listener
 * added/removed per gesture — see CLICK_SUPPRESSION_WINDOW_MS above) says the click currently in
 * flight should be killed.
 */
const isSuppressingClick = () => Date.now() < drawerConfiguration.suppressClickUntil;

/**
 * Kills the click event currently in flight, in the capture phase (before any bubble-phase
 * listener inside the drawer, such as a card's own click handler, ever sees it). Clears the flag
 * immediately so it can't also swallow some later, unrelated click still inside the same window —
 * an interrupted gesture (or a pointer type that never produces a following click at all) just
 * lets the timestamp lapse on its own instead, no explicit disarm/cleanup needed.
 * @param {MouseEvent} event
 */
const suppressClick = (event) => {
  event.preventDefault();
  event.stopPropagation();
  drawerConfiguration.suppressClickUntil = 0;
};

/**
 * Scoped to the drawer element itself (the click this needs to catch can only ever land inside
 * it) and running in the capture phase, ahead of anything the drawer's own content might attach.
 */
const drawerClickSuppression$ = onDrawerClick$.pipe(
  filter(isSuppressingClick),
  tap(suppressClick),
);

/**
 * Handles the pointer up event on the window to complete a drag gesture for the drawer.
 *
 * - Checks if a drag operation is in progress (isDragging).
 * - Ends the drag operation and restores the drawer's CSS transition.
 * - Cancels any pending animation frame for position updates.
 * - If the drawer was dragged beyond the configured threshold, closes the drawer.
 *   Otherwise, snaps the drawer back to its original position.
 * - Resets the currentY value in the drawer configuration.
 *
 * This function finalizes the drag-to-close gesture, either closing the drawer or restoring its position.
 *
 * onTap() (not onSwipeDown()) synchronously changes the drawer's layout — e.g. expanding out of
 * the collapsed peek state. The browser's click event that follows this same tap on a touch
 * device fires strictly after this handler returns, and resolves its target against that
 * already-changed layout instead of whatever was actually under the finger — so a tap that only
 * meant to expand the drawer can also "click" content newly revealed underneath it (e.g. a card
 * in a grid that starts right below the collapsed peek strip). preventDefault() on the earlier
 * pointerdown is supposed to suppress that per the Pointer Events spec, but isn't reliably
 * honored across every touch browser this needs to support — so the actual click that follows is
 * caught and killed directly instead, via suppressClickUntil/drawerClickSuppression$ above.
 */
const handleWindowPointerUp = () => {
  if (!drawerConfiguration.isDragging) {
    return;
  }

  drawerConfiguration.isDragging = false;
  drawerNode.classList.remove('nhs-drawer--dragging');

  cancelAnimationFrame(drawerConfiguration.animationFrameId);
  drawerConfiguration.animationFrameId = null;

  if (drawerConfiguration.currentY > drawerConfiguration.threshold) {
    (drawerConfiguration.strategy.onSwipeDown ?? closeDrawer)();
  } else {
    // onTap() unconditionally clears the collapsed state — including when the drawer was
    // already expanded (just not full-size yet), where it's a no-op that changes no layout.
    // Only suppress the click for the genuine collapsed→expanded transition (data-drawer-open
    // is the stable, externally-observable mirror of that state — see setDrawerExpanded); any
    // other tap (e.g. a real card click while already expanded) must pass its click through
    // untouched.
    const wasCollapsed = drawerNode.dataset.drawerOpen === 'false';
    if (wasCollapsed) {
      drawerConfiguration.suppressClickUntil = Date.now() + CLICK_SUPPRESSION_WINDOW_MS;
    }
    drawerConfiguration.strategy.onTap();
  }

  drawerConfiguration.currentY = 0;
};

const mountElement = ({ value: element, options = {} }) => {
  const callbackName = selectDrawer(store.getState())?.name;
  const callbacksRegistered = registeredCallbacks.get(callbackName) ?? [];

  drawerConfiguration.strategy = options.peek ? createPeekStrategy(options.peek) : createDefaultStrategy();

  drawerContainer.innerHTML = '';
  drawerContainer.appendChild(element);
  drawerConfiguration.strategy.activate();
  if (!options.peek) {
    overlay.create();
  }
  callbacksRegistered.filter((fn) => typeof fn === 'function').forEach((fn) => fn());
};

const close = drawer$.pipe(
  filter((data) => data.action === 'clearHtml'),
  filter(isDrawerReady),
  tap(closeDrawer)
);

const disconnect = drawer$.pipe(
  filter((data) => data.action === 'onHtmlRemoved'),
  filter(isDrawerReady),
  tap(disconnectCallback)
);

const open = drawer$.pipe(
  filter((data) => data.action === 'setHtml'),
  filter(isDrawerReady),
  tap(openDrawer)
);

const register = drawer$.pipe(
  filter((data) => data.action === 'onHtmlReady'),
  filter(isDrawerReady),
  tap(registerCallback)
);

const mount = drawer$.pipe(
  filter((data) => data.action === 'mountElement'),
  filter(isDrawerReady),
  tap(mountElement)
);

const expandToFullSize = () => {
  drawerConfiguration.isFullSize = true;
  drawerNode.classList.remove('nhs-drawer--collapsed');
  drawerNode.classList.add('nhs-drawer--full');
  setDrawerExpanded(true);
};

const collapseToPartial = () => {
  drawerConfiguration.isFullSize = false;
  drawerNode.classList.remove('nhs-drawer--full');
};

const expandFull = drawer$.pipe(
  filter((data) => data.action === 'expandToFullSize'),
  filter(isDrawerReady),
  tap(expandToFullSize)
);

const collapsePartial = drawer$.pipe(
  filter((data) => data.action === 'collapseToPartial'),
  filter(isDrawerReady),
  tap(collapseToPartial)
);

merge(close, disconnect, open, register, mount, expandFull, collapsePartial).subscribe();
onDrawerPointerDown$.subscribe(handleDrawerPointerDown);
onDocumentPointerMove$.subscribe(handleWindowPointerMove);
onDocumentPointerUp$.subscribe(handleWindowPointerUp);
drawerClickSuppression$.subscribe();
